import { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
  incidentArtifactService,
  ARTIFACT_LABELS,
  ARTIFACT_ORDER,
} from '../../../services/incidentArtifactService';
import { trackIncidentArtifactChange } from '../../../utils/modernUITracking';
import styles from './IncidentArtifactLinks.module.css';

const ROWS = ARTIFACT_ORDER.map((kind) => ({ kind, label: ARTIFACT_LABELS[kind] }));

const emptyRow = { saved: '', draft: '', busy: false, error: '', notice: '' };
const emptyRows = () => Object.fromEntries(ARTIFACT_ORDER.map((kind) => [kind, emptyRow]));

/**
 * IncidentArtifactLinks
 *
 * Lets an Eternium member attach, replace or clear the Exec Summary Slide and Document links of
 * a resolved incident (US2163904, US2174775). Only the page decides when it is rendered, and it
 * renders it for Eternium alone, below the Executive Summary row everyone sees.
 *
 * It does not fetch. The page owns the one load of the stored links (useIncidentArtifacts) and
 * passes it in, so Eternium makes one request and the row above follows a Save or Clear through
 * `onLinkChange` with no refetch. A row's saved value always follows `links`; its draft follows
 * only while the row has no unsaved edit.
 *
 * Render it with `key={problemId}` so switching incident starts from empty rows: the fields
 * carry drafts and notices that must not leak from one incident to the next.
 *
 * @param {Object} props
 * @param {string} props.problemId - Problem number
 * @param {{kind: string, url: string}[]} props.links - stored links, from the page
 * @param {boolean} props.loading - the page's load is in flight
 * @param {string} props.loadError - the page's load failed; user-facing message
 * @param {() => void} props.onRetry - load again
 * @param {(kind: string, url: string) => void} props.onLinkChange - a save (url) or clear ('')
 * @param {Object} [props.newrelic] - NewRelic browser agent instance
 */
function IncidentArtifactLinks({ problemId, links, loading, loadError, onRetry, onLinkChange, newrelic }) {
  const [rows, setRows] = useState(emptyRows);

  const patchRow = useCallback((kind, patch) => {
    setRows((prev) => ({ ...prev, [kind]: { ...prev[kind], ...patch } }));
  }, []);

  useEffect(() => {
    setRows((prev) => {
      const next = { ...prev };
      ARTIFACT_ORDER.forEach((kind) => {
        const url = (links || []).find((l) => l.kind === kind)?.url || '';
        const row = prev[kind];
        const dirty = row.draft !== row.saved;
        next[kind] = { ...row, saved: url, draft: dirty ? row.draft : url };
      });
      return next;
    });
  }, [links]);

  const handleSave = async (kind) => {
    patchRow(kind, { busy: true, error: '', notice: '' });
    try {
      const saved = await incidentArtifactService.saveArtifact(problemId, kind, rows[kind].draft);
      patchRow(kind, { busy: false, saved: saved.externalUrl, draft: saved.externalUrl, notice: 'Saved' });
      if (onLinkChange) onLinkChange(kind, saved.externalUrl);
      trackIncidentArtifactChange(problemId, kind, 'save', 'ok', newrelic);
    } catch (err) {
      patchRow(kind, { busy: false, error: err.message });
      trackIncidentArtifactChange(problemId, kind, 'save', 'error', newrelic);
    }
  };

  const handleClear = async (kind) => {
    patchRow(kind, { busy: true, error: '', notice: '' });
    try {
      await incidentArtifactService.clearArtifact(problemId, kind);
      patchRow(kind, { busy: false, saved: '', draft: '', notice: 'Removed' });
      if (onLinkChange) onLinkChange(kind, '');
      trackIncidentArtifactChange(problemId, kind, 'clear', 'ok', newrelic);
    } catch (err) {
      patchRow(kind, { busy: false, error: err.message });
      trackIncidentArtifactChange(problemId, kind, 'clear', 'error', newrelic);
    }
  };

  // A failed load leaves every row empty, which would read as "no link" and let a Save replace
  // one the user never saw. Nothing is editable until the stored links have loaded.
  const rowsLocked = loading || !!loadError;

  return (
    <section className={styles.container} aria-labelledby="incident-artifact-links-title">
      <h2 id="incident-artifact-links-title" className={styles.title}>
        Exec Summary Slide and Document links
      </h2>
      <p className={styles.help}>
        Only Eternium edits these links. Links must be on coxautoinc.sharepoint.com.
      </p>

      {loadError && (
        <>
          <p className={styles.error} role="alert">
            {loadError}
          </p>
          <Button variant="outline-secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </>
      )}

      {ROWS.map(({ kind, label }) => {
        const row = rows[kind];
        const inputId = `incident-artifact-${kind}`;
        const unchanged = row.draft.trim() === row.saved;
        return (
          <div className={styles.row} key={kind}>
            <label htmlFor={inputId} className={styles.label}>
              {label}
            </label>
            <div className={styles.controls}>
              <input
                id={inputId}
                type="url"
                className={styles.input}
                value={row.draft}
                placeholder="https://coxautoinc.sharepoint.com/..."
                disabled={rowsLocked || row.busy}
                onChange={(e) => patchRow(kind, { draft: e.target.value, error: '', notice: '' })}
              />
              <Button
                variant="primary"
                size="sm"
                className={styles.button}
                disabled={rowsLocked || row.busy || unchanged || row.draft.trim() === ''}
                onClick={() => handleSave(kind)}
                aria-label={`Save ${label}`}
              >
                Save
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className={styles.button}
                disabled={rowsLocked || row.busy || row.saved === ''}
                onClick={() => handleClear(kind)}
                aria-label={`Clear ${label}`}
              >
                Clear
              </Button>
            </div>
            {row.error && (
              <p className={styles.error} role="alert">
                {row.error}
              </p>
            )}
            {row.notice && !row.error && (
              <p className={styles.notice} role="status">
                {row.notice}
              </p>
            )}
          </div>
        );
      })}
    </section>
  );
}

export default IncidentArtifactLinks;
